import { FC, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { LatLngBoundsLiteral } from "leaflet";

import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Button,
  Card,
  Grid,
  Stack,
  Typography,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";

import QueryData from "Constants/QueryData";
import { IPath } from "Types/path";
import { IService } from "Types/service";

import useCancelOperation from "Hooks/useCancelOperation";
import { useAppDispatch, useAppSelector } from "Hooks/useRedux";

import { setMapData } from "Redux/mapSlice";
import { changeOperation } from "Redux/operationSlice";

import Api from "Api";

import MapContainer from "Components/MapContainer";
import CustomModal from "Components/Modal";
import type { IDay } from "Components/WeeklyData";
import Points from "Pages/Home/Map/Points";
import Stations from "Pages/Home/Map/Stations";
import Route from "Pages/Home/Map/Route";
import type { IChildrenProps } from "..";
import General from "./General";
import Price from "./Price";
import Time from "./Time";
import StationTiming from "./StationTiming";

export interface IStepProps {
  data: { [key: string]: any } | null;
  setData: (name: string, value: any) => void;
}
interface IStep {
  id: string;
  title: string;
  content: FC<IStepProps>;
}
interface IDayData {
  service_id: number;
  week_day: number;
  start_time?: string;
  end_time?: string;
}

interface IDayData2 {
  service_id: number;
  source_station_id?: number;
  week_day: number;
  start_time?: string;
  end_time?: string;
}

const Steps: IStep[] = [
  { id: "general", title: "اطلاعات کلی و ترتیب ایستگاه‌ها", content: General },
  { id: "time", title: "سرفاصله اعزام ناوگان", content: Time },
  { id: "stationTiming", title: "زمانبندی‌ ایستگاه‌ها", content: StationTiming },
  { id: "price", title: "تخمین قیمت", content: Price },
];

const handleWeeklyData = (data: IDayData[]) => {
  const result = [];
  for (let i = 0; i <= 7; i += 1) {
    const dayData = data?.filter((d) => d.week_day === i);

    if (dayData?.length > 0) {
      const times: IDayData[] = [];
      dayData.forEach((item) => {
        times.push(item);
      });
      result.push({ id: i, times });
    }
  }
  return result;
};

export default function CreatePath({ closeModal, isOpen }: IChildrenProps) {
  const { ids, stations, distance } = useAppSelector((state) => state.stations);
  const { points } = useAppSelector((state) => state.points);
  const { type, name } = useAppSelector((state) => state.operation);
  const appData = useAppSelector((state) => state.app);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [dialogText, setDialogText] = useState<string>("");

  const isEdit = name === "edit-path";

  const dispatch = useAppDispatch();

  const { cancelOperation } = useCancelOperation();

  const queryClient = useQueryClient();

  const [step, setStep] = useState<number>(0);
  const [data, setData] = useState<{ [key: string]: { [key: string]: any } }>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showMessage, setShowMessage] = useState<boolean>(false);

  const handleCloseDialog = () => setOpenDialog(false);

  // Api functions
  const handleApiError = () => {
    setIsLoading(false);
  };
  const handleCreatePath = () => {
    setIsLoading(true);

    const { general, time, price, stationTiming } = data;

    // 1- create the path
    Api.post("path", { name: general?.name, points, stations: ids, type })
      .then((firstRes) => {
        const firstData = firstRes.data.Data;
        // 2- add service plan
        if (general?.working_days?.length > 0)
          Api.post("trip-plan/service-plan", { path_id: firstData.id, working_days: general?.working_days, status: 1 })
            .then((secondRes) => {
              const secondData = secondRes.data.Data;
              // 3- add service times
              const timesDayData: IDay[] = time.data;
              const intervals: IDayData[] = [];

              timesDayData.forEach((day) => {
                day.times.forEach((i) => {
                  intervals.push({
                    service_id: secondData.id,
                    week_day: day.id,
                    ...i,
                  });
                });
              });
              if (intervals?.length > 0)
                Api.post("trip-plan/dispatch-interval", { intervals })
                  .then(() => {
                    // 4- add service prices
                    const stationTimingDayData: IDay[] = stationTiming.data;
                    const times: IDayData2[] = [];
                    stationTimingDayData.forEach((day) => {
                      day.times.forEach((t) => {
                        times.push({
                          service_id: secondData.id,
                          week_day: day.id,
                          ...t,
                        });
                      });
                    });
                    if (times?.length > 0)
                      Api.post(`trip-plan/service-time`, { times })
                        .then(() => {
                          const priceDayData: IDay[] = price.data;
                          const pricing: IDayData[] = [];
                          priceDayData.forEach((day) => {
                            day.times.forEach((i) => {
                              pricing.push({
                                service_id: secondData.id,
                                week_day: day.id,
                                ...i,
                              });
                            });
                          });
                          if (pricing?.length > 0)
                            Api.post("trip-plan/service-pricing", { pricing })
                              .then(() => {
                                queryClient.invalidateQueries({ queryKey: [QueryData.SERVICE.key] });
                                setShowMessage(true);
                                setIsLoading(false);
                              })
                              .catch(() => {
                                return handleApiError();
                              });
                        })
                        .catch(() => {
                          return handleApiError();
                        });
                  })
                  .catch(() => {
                    return handleApiError();
                  });
            })
            .catch(() => {
              return handleApiError();
            });
        queryClient.invalidateQueries({ queryKey: [QueryData.SERVICE.key] });
        setShowMessage(true);
        setIsLoading(false);
      })
      .catch((err1) => {
        return handleApiError();
      });
  };
  const handleEditPath = () => {
    setIsLoading(true);

    const { general, time, price } = data;

    // 1- create the path
    Api.put(`path/${appData.path.service.path_id}`, { name: general?.name, points, stations: ids, type })
      .then(() => {
        // 2- add service plan
        if (general?.working_days?.length > 0 && appData.path?.service?.path_id)
          Api.put(`trip-plan/service-plan/${appData.path.id}`, {
            path_id: appData.path.service?.path_id,
            working_days: general?.working_days,
            status: 1,
          })
            .then(() => {
              // 3- add service times
              const timesDayData: IDay[] = time.data;
              const intervals: IDayData[] = [];
              timesDayData.forEach((day) => {
                day.times.forEach((i) => {
                  intervals.push({
                    service_id: appData.path.id,
                    week_day: day.id,
                    ...i,
                  });
                });
              });

              if (intervals?.length > 0)
                Api.put(`trip-plan/dispatch-interval/${appData.path.id}`, { intervals })
                  .then(() => {
                    // 4- add service prices
                    const priceDayData: IDay[] = price.data;
                    const pricing: IDayData[] = [];
                    priceDayData.forEach((day) => {
                      day.times.forEach((i) => {
                        pricing.push({
                          service_id: appData.path.id,
                          week_day: day.id,
                          ...i,
                        });
                      });
                    });

                    if (pricing?.length > 0)
                      Api.put(`trip-plan/service-pricing/${appData.path.id}`, { pricing })
                        .then(() => {
                          queryClient.invalidateQueries({ queryKey: [QueryData.SERVICE.key] });
                          setShowMessage(true);
                          setIsLoading(false);
                        })
                        .catch(() => handleApiError());
                  })
                  .catch(() => handleApiError());
            })
            .catch(() => handleApiError());
        queryClient.invalidateQueries({ queryKey: [QueryData.SERVICE.key] });
        setShowMessage(true);
        setIsLoading(false);
      })
      .catch(() => handleApiError());
  };

  // Logic functions
  const handleChangeStep = (input: "next" | "prev") => {
    if (input === "next") {
      if (step === 0) {
        // if (!data.general || data.general?.name === "") {
        //   setDialogText("لطفا نام خط را وارد کنید");
        //   setOpenDialog(true);
        // } else if (!data.general?.working_days || data.general?.working_days?.length === 0) {
        //   setDialogText("لطفا روزهای فعالیت را وارد کنید");
        //   setOpenDialog(true);
        // } else {
        //   setStep((prev) => prev + 1);
        // }
        setStep((prev) => prev + 1);
      } else if (step === 1) {
        // if (data.time?.data.length === 0 || (data.time?.data.length === 1 && data.time?.data[0].times.length === 0)) {
        //   setDialogText("لطفا حداقل یک سرفاصله ایجاد کنید");
        //   setOpenDialog(true);
        // } else {
        //   setStep((prev) => prev + 1);
        // }
        setStep((prev) => prev + 1);
      } else if (step === 2) {
        setStep((prev) => prev + 1);
      }
      //  else if (
      //   step === 3 &&
      //   (data.price?.data?.length === 0 || (data.price?.data?.length === 1 && data.price?.data[0]?.times?.length === 0))
      // ) {
      //   setDialogText("لطفا حداقل یک قیمت ایجاد کنید");
      //   setOpenDialog(true);
      // }
      else if (isEdit) handleEditPath();
      else {
        handleCreatePath();
      }
    } else if (step === 0) closeModal();
    else setStep((prev) => prev - 1);
  };
  const handleSetData = (key: string, value: any) => {
    const stepId = Steps[step].id;
    const stepData = data[`${stepId}` as keyof typeof data];
    setData((prev) => ({ ...prev, [`${stepId}`]: { ...stepData, [key]: value } }));
  };
  const handleGoForStationTiming = () => {
    cancelOperation();
    const positions: LatLngBoundsLiteral = stations.map((station) => [station.lat, station.lng]);
    dispatch(setMapData({ bounds: { positions } }));
    dispatch(changeOperation({ name: "station-timing", type }));
    closeModal();
  };
  const handleCancelOperation = () => {
    closeModal();
    cancelOperation();
  };

  const renderSteps = useMemo(() => {
    const stepData = Steps[step];
    const Content = stepData.content;

    return (
      <Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
          <Stack spacing={1}>
            <Typography variant="h4" fontWeight="800">
              {stepData.title}
            </Typography>
            <Typography variant="subtitle2">شما در حال ثبت مسیر هستید...</Typography>
          </Stack>

          <Box sx={{ bgcolor: "#282828", color: "#fff", fontSize: "12px", p: 1, borderRadius: 1 }}>
            <Stack direction="row" spacing={0.5}>
              <Typography fontSize="inherit">طول کل مسیر:</Typography>
              <Typography fontSize="inherit" className="fa-number" sx={{ direction: "rtl" }}>
                {Math.round((distance / 1000) * 10) / 10} KM
              </Typography>
            </Stack>
          </Box>
        </Stack>

        <Content data={data || null} setData={handleSetData} />
      </Stack>
    );
  }, [step, data]);
  const renderStepButtons = useMemo(() => {
    return (
      <Grid container mt={5} columnSpacing={1} justifyContent="flex-end">
        <Grid item xs={2.5}>
          <Button fullWidth size="large" variant="light" onClick={() => handleChangeStep("prev")}>
            {step === 0 ? "انصراف" : "قبلی"}
          </Button>
        </Grid>
        <Grid item xs={3}>
          <LoadingButton loading={isLoading} fullWidth size="large" variant="contained" onClick={() => handleChangeStep("next")}>
            {step + 1 < Steps.length ? "ادامه" : <>{isEdit ? "ذخیره تغییرات" : "ثبت مسیر"}</>}
          </LoadingButton>
        </Grid>
      </Grid>
    );
  }, [step, handleChangeStep, isLoading]);

  useEffect(() => {
    if (isOpen && appData.path) {
      const { path } = appData;
      const { service } = appData.path;
      const time = handleWeeklyData(service?.intervals);
      const price = handleWeeklyData(service?.pricings);
      const stationTiming = handleWeeklyData(service?.times);

      setData({
        general: { working_days: service?.working_days, name: path.name },
        time: { data: time },
        price: { data: price },
        stationTiming: { data: stationTiming },
      });
    } else {
      setData({});
      setStep(0);
      setShowMessage(false);
    }
  }, [isOpen, appData]);

  return (
    <CustomModal open={isOpen} onClose={closeModal} size={showMessage ? "xs" : "lg"}>
      <Card sx={{ p: 3 }}>
        {showMessage ? (
          <Stack spacing={3} alignItems="center">
            <Box
              sx={{
                width: "100px",
                height: "100px",
                borderRadius: "50%",
                bgcolor: "#F1F2FF",
                color: "primary.main",
                display: "grid",
                placeItems: "center",
              }}
            >
              <DoneIcon sx={{ fontSize: "50px" }} />
            </Box>

            <Stack alignItems="center" spacing={1}>
              <Typography variant="h5" sx={{ fontWeight: "800" }}>
                {isEdit ? "مسیر با موفقیت ویرایش شد!" : "مسیر شما با موفقیت ساخته شد!"}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
              <Button size="large" fullWidth variant="light" onClick={handleCancelOperation}>
                تمام
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Grid container columnSpacing={3}>
            <Grid item xs={4}>
              <Box sx={{ width: "100%", height: "100%", borderRadius: 2, overflow: "hidden" }}>
                {/* <MapContainer dragging={false} zoomable={false} /> */}
                <MapContainer dragging={true}>
                  <Points />
                  <Stations minimize={true} />
                  <Route />
                </MapContainer>
              </Box>
            </Grid>
            <Grid item xs={8}>
              {renderSteps}

              {renderStepButtons}
            </Grid>
          </Grid>
        )}
      </Card>
    </CustomModal>
  );
}
